import { Theme, DeprecatedTooltip } from "@landtechnologies/components";
import clsx from "clsx";
import React, { FC, HTMLProps } from "react";
import { Currency, Translation, formatToCurrency, useTranslation } from "react-migration/lib";
import { Feature, userActions } from "src/js/stores/user";
import { MeasurementSystem } from "src/js/stores/user/store";
import { SupportedLocale } from "src/js/util/dates";
import { PriceModes, PriceTypes } from "react-migration/domains/comparables/typings";
import { isZoopla } from "react-migration/domains/comparables/util/isZoopla";
import { isPerAreaMode } from "react-migration/domains/comparables/util/isPerAreaMode";

export interface ResultsProps extends HTMLProps<HTMLDivElement> {
  areaSet: boolean;
  loading: boolean;
  totalResults: number;
  floorAreaResults: number;
  soldAverage: number;
  soldResults: number;
  marketAverage: number;
  marketResults: number;
  currency?: Currency;
  unitPreference: MeasurementSystem;
  locale: SupportedLocale;
  priceMode: PriceModes;
  priceType: PriceTypes;
}

interface ResultProps {
  className?: string;
  currency?: Currency;
  priceMode: PriceModes;
  unitPreference: MeasurementSystem;
  title: React.ReactNode;
  amount: React.ReactNode;
  locale: SupportedLocale;
  results: number;
  theme?: Theme;
}

function getAmount(
  amount: React.ReactNode,
  pricePerAreaFormatted: (amount: number) => string | undefined
) {
  if (typeof amount === "number") {
    return amount === 0 ? "-" : pricePerAreaFormatted(amount);
  }

  return amount;
}

export const Result: FC<ResultProps> = ({
  className,
  currency,
  locale,
  priceMode,
  title,
  unitPreference,
  amount,
  results,
  theme = Theme.Dark,
}) => {
  const { t } = useTranslation();
  const pricePerAreaFormatted = (amount: number) => {
    return formatToCurrency({
      currency,
      locale,
      amount,
    });
  };

  return (
    <div
      className={clsx(
        `atlas-flex-1-0 atlas-w-1/2 atlas-flex atlas-flex-col atlas-items-center`,
        className
      )}
    >
      <div
        className={clsx(
          "atlas-font-bold",
          theme === Theme.Dark
            ? "atlas-text-content-inverse-secondary"
            : "atlas-text-content-secondary"
        )}
      >
        {title}
      </div>
      <div
        className={clsx(
          `atlas-text-xl atlas-font-bold atlas-py-1`,
          theme === Theme.Dark ? "atlas-text-lightGreen-500" : "atlas-text-content-secondary"
        )}
      >
        {getAmount(amount, pricePerAreaFormatted)}
      </div>
      <div
        className={clsx(
          "atlas-text-xs",
          theme === Theme.Dark
            ? "atlas-text-content-inverse-secondary"
            : "atlas-text-content-secondary",
          isPerAreaMode(priceMode) ? undefined : "atlas-opacity-0"
        )}
      >
        {t("comparables.calculator.results.per")}{" "}
        {unitPreference === MeasurementSystem.METRIC
          ? t("comparables.sqm.alt")
          : t("comparables.sqft.alt")}
      </div>
      <div
        className={clsx(
          "atlas-text-xs",
          theme === Theme.Dark
            ? "atlas-text-content-inverse-secondary"
            : "atlas-text-content-secondary"
        )}
      >
        {t("comparables.calculator.results.messages.total", { total: results })}
      </div>
    </div>
  );
};

export interface IMessageText {
  t: Translation;
  areaSet: boolean;
  loading: boolean;
  totalResults: number;
  floorAreaResults: number;
  soldAverage: number;
  marketAverage: number;
  priceType: PriceTypes;
}
export const getMessageText = ({
  t,
  loading,
  areaSet,
  soldAverage,
  marketAverage,
  priceType,
  totalResults,
  floorAreaResults,
}: IMessageText): string => {
  if (loading) {
    return t("comparables.calculator.results.messages.calculating");
  } else if (!areaSet) {
    return t("comparables.calculator.results.messages.area");
  } else if (soldAverage || marketAverage) {
    if (priceType === PriceTypes.PER_AREA) {
      return t("comparables.calculator.results.messages.results.ppa", {
        total: totalResults,
        floor: floorAreaResults,
      });
    } else {
      return t("comparables.calculator.results.messages.results", {
        total: totalResults,
      });
    }
  } else {
    return t("comparables.calculator.results.messages.none");
  }
};

export const Message = ({
  loading,
  areaSet,
  soldAverage,
  marketAverage,
  priceType,
  totalResults,
  floorAreaResults,
}: Omit<IMessageText, "t">) => {
  const { t } = useTranslation();
  return (
    <span>
      {getMessageText({
        t,
        loading,
        areaSet,
        soldAverage,
        marketAverage,
        priceType,
        totalResults,
        floorAreaResults,
      })}
      {totalResults >= 200 && (
        <DeprecatedTooltip
          theme={Theme.Dark}
          placement="top"
          description={t("comparables.calculator.results.messages.limit")}
        >
          <i className="icon-lt-information-line atlas-ml-1" />
        </DeprecatedTooltip>
      )}
    </span>
  );
};

export const Results: FC<ResultsProps> = ({
  areaSet,
  loading,
  totalResults,
  floorAreaResults,
  soldAverage,
  soldResults,
  marketAverage,
  marketResults,
  currency,
  unitPreference,
  locale,
  priceMode,
  priceType,
}) => {
  const { t } = useTranslation();

  return (
    <div data-testid="calc-results">
      <div className="atlas-mb-2 atlas-text-xs">
        <Message
          areaSet={areaSet}
          loading={loading}
          totalResults={totalResults}
          floorAreaResults={floorAreaResults}
          soldAverage={soldAverage}
          marketAverage={marketAverage}
          priceType={priceType}
        />
      </div>
      <div className="atlas-flex atlas-gap-8 atlas-bg-content-secondary atlas-p-2">
        <Result
          currency={currency}
          locale={locale}
          priceMode={priceMode}
          title={
            isZoopla(priceMode)
              ? t("comparables.calculator.zoopla_results.last_value")
              : t("comparables.calculator.results.sold")
          }
          amount={loading ? 0 : soldAverage}
          unitPreference={unitPreference}
          results={soldResults}
        />
        <Result
          currency={currency}
          locale={locale}
          priceMode={priceMode}
          title={
            <>
              {t("comparables.calculator.results.market")}
              {userActions.hasFeature(Feature.usAccess) ? null : (
                <DeprecatedTooltip
                  theme={Theme.Dark}
                  placement="top"
                  description={
                    isZoopla(priceMode)
                      ? t("comparables.calculator.results.market.zoopla_tooltip")
                      : t("comparables.calculator.results.market.tooltip")
                  }
                >
                  <i className="icon-lt-information-line atlas-ml-1" />
                </DeprecatedTooltip>
              )}
            </>
          }
          unitPreference={unitPreference}
          amount={loading ? 0 : marketAverage}
          results={marketResults}
        />
      </div>
    </div>
  );
};
